// src/views/admin/users/chefs/view/ChefViewPage.jsx
import React, { useEffect, useState } from 'react';
import { useParams }   from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  RiUserLine,
  RiLockLine,
  RiMapPinLine,
  RiTimeLine,
  RiCalendarCheckLine,
  RiImageEditLine,
  RiMoneyDollarCircleLine,
} from 'react-icons/ri';

import Breadcrumb               from '@/components/common/Breadcrumb';
import Card                     from '@/components/ui/cards/Card';
import ToastMe                  from '@/components/ui/ToastMe';
import {
  /* legacy helper */
  getFileUrl,
  /* signed‑URL helper (adds ?tk=<jwt>, URI‑encodes the name) */
  getFileUrlSigned,
} from '@/services/CommonService';
import {
  getByIdAdminService,
  updateAdminPhotoService,
} from '@/services/AdminServices';

import ProfileTab      from '@/views/admin/users/employees/view/tabs/ProfileTab';
import SecurityTab     from '@/views/admin/users/employees/view/tabs/SecurityTab';
import ChefAddressTab  from './tabs/AddressTab';
import AttendedTab     from './tabs/AttendedTab';
import LeaveTab        from './tabs/LeaveTab';
import PayrollTab      from './tabs/PayrollTab';

const TAB_ITEMS = [
  { label: 'Profile',    key: 'profile',    Icon: RiUserLine        },
  { label: 'Security',   key: 'security',   Icon: RiLockLine        },
  { label: 'Address',    key: 'address',    Icon: RiMapPinLine      },
  { label: 'Attendance', key: 'attendance', Icon: RiTimeLine       },
  { label: 'Leave',      key: 'leave',      Icon: RiCalendarCheckLine },
  { label: 'Payroll',    key: 'payroll',    Icon: RiMoneyDollarCircleLine },
];

export default function ChefViewPage({ pageTitle = 'Chef / View' }) {
  const { id: chefId } = useParams();
  const dispatch       = useDispatch();

  const [chef,    setChef]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab,     setTab]     = useState('profile');

  useEffect(() => {
    dispatch(getByIdAdminService(chefId))
      .then(res => setChef(res.data?.data))
      .catch(err => ToastMe(err.message || 'Failed to load chef', 'error'))
      .finally(() => setLoading(false));
  }, [chefId, dispatch]);

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !chef?._id) return ToastMe('No chef found', 'error');

    const fd = new FormData();
    fd.append('photo', file);

    try {
      await dispatch(updateAdminPhotoService(chef._id, fd));
      ToastMe('Profile photo updated', 'success');
      // re-fetch to get updated file_name
      const fresh = await dispatch(getByIdAdminService(chef._id));
      setChef(fresh.data?.data);
    } catch (err) {
      ToastMe(err.message || 'Upload failed', 'error');
    }
  };

  if (loading) return <Card className="p-6 text-center">Loading…</Card>;
  if (!chef)   return <Card className="p-6 text-center">No chef found.</Card>;

  // build avatar URL with cache‑bust
  const baseAvatar = chef.file_name
    ? getFileUrlSigned(chef.file_name, 'admins')
    : null;
  const avatarUrl = baseAvatar
    ? `${baseAvatar}${baseAvatar.includes('?') ? '&' : '?'}t=${Date.now()}`
    : '/images/default-avatar.png';

  return (
    <>
      <div className="pt-[18px]">
        <Breadcrumb pageTitle={pageTitle} />
      </div>

      <Card className="rounded-xl border border-[#f0f2f8] bg-white p-6">
        <div className="flex gap-6 items-start">
          <div className="shrink-0">
            <div className="w-[120px] h-[120px] rounded-[6px] overflow-hidden border border-slate-200">
              <img
                src={avatarUrl}
                alt={chef.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="flex flex-col justify-start">
            <h2 className="text-[28px] leading-8 font-bold tracking-tight text-slate-900">
              {chef.name}
            </h2>
            <span className="mt-2 inline-block w-max rounded-[4px] bg-[#f8e8b7] px-[10px] py-[3px] text-[11px] font-semibold uppercase text-[#b38a00] tracking-wide">
              {chef.role_id?.name || 'Chef'}
            </span>

            <button
              onClick={() => document.getElementById('photoUpload').click()}
              className="mt-6 inline-flex items-center justify-center gap-2 h-[44px] w-[220px] rounded-[6px] text-[15px] font-semibold text-white bg-[#0065ff] hover:bg-[#0056e0] transition"
            >
              <RiImageEditLine className="text-[20px]" />
              Upload New Photo
            </button>
            <input
              id="photoUpload"
              type="file"
              accept="image/*"
              hidden
              onChange={handlePhotoChange}
            />
          </div>
        </div>
      </Card>

      <div className="mt-6 px-6">
        <ul className="flex items-center space-x-6">
          {TAB_ITEMS.map(({ key, label, Icon }) => (
            <li key={key}>
              <button
                onClick={() => setTab(key)}
                className={`inline-flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-lg transition ${
                  tab === key ? 'bg-[#0f62fe] text-white' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Icon className="text-base" />
                {label}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-6">
        {tab === 'profile'    && <ProfileTab  employee={chef}        />}
        {tab === 'security'   && <SecurityTab employeeId={chef._id} />}
        {tab === 'address'    && <ChefAddressTab  chefId={chef._id} />}
        {tab === 'attendance' && <AttendedTab   employeeId={chef._id} />}
        {tab === 'leave'      && <LeaveTab      employeeId={chef._id} />}
        {tab === 'payroll'    && <PayrollTab    employeeId={chef._id} />}
      </div>
    </>
  );
}
